import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { useAnimate, useInView } from "framer-motion";
import { AspectRatio, Box, Flex, type BoxProps } from "@chakra-ui/react";
import Image from "next/image";
import { ChakraBox } from "ui/chakra-framer";
import funnelsMobile from "../../../../../../public/assets/platform/campaign-intelligence/sync-funnels-mobile.png";
import { Tooltip } from "./tooltip";
import { DestinationMenu } from "./destination-menu";
import { FunnelsGraph } from "./graph";

const fadeIn = { opacity: 1 };
const fadeOut = { opacity: 0 };
const fast = { duration: 0.15 };
const iconTransition = { at: "-0.25", duration: 0.25 };
const resetTransition = { at: "-0.1", duration: 0.1 };

function useSyncFunnelsAnimation(isActive: boolean) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        await animate(
          isActive
            ? [
                // Animation sequence
                ["#funnels-tooltip", { scale: 1, ...fadeIn }, { at: 1, type: "spring", damping: 30, stiffness: 1000 }],
                ["#funnels-tooltip-link", { color: "#40e398" }, { at: "+0.9", ...fast }],
                ["#funnels-tooltip-chevron", { x: "3px" }, { at: "-0.2", ...fast }],
                [
                  "#funnels-tooltip",
                  { scale: 0.9, ...fadeOut },
                  { at: "+0.6", type: "spring", damping: 30, stiffness: 500 },
                ],
                ["#funnels-menu", { scale: 1, ...fadeIn }, { type: "spring", damping: 30, stiffness: 1000 }],
                ["#funnels-menu-spinner", { opacity: [0, 1, 0] }, { duration: 1.5 }],
                ["#funnels-fb--mono", fadeOut, { duration: 0.25 }],
                ["#funnels-fb--color", fadeIn, iconTransition],
                ["#funnels-ga--color", fadeIn, iconTransition],
                ["#funnels-ga--mono", fadeOut, iconTransition],
                ["#funnels-tiktok--color", fadeIn, iconTransition],
                ["#funnels-tiktok--mono", fadeOut, iconTransition],
                ["#funnels-check-svg", fadeIn, { at: "-0.2", type: "spring", damping: 30, stiffness: 1000 }],
              ]
            : [
                // Reset
                ["#funnels-tooltip", { scale: 0.9, ...fadeOut }, { duration: 0.1 }],
                ["#funnels-tooltip-link", { color: "#dbe1e8" }, resetTransition],
                ["#funnels-tooltip-chevron", { x: 0 }, resetTransition],
                ["#funnels-menu", { scale: 0.9, ...fadeOut }, resetTransition],
                ["#funnels-menu-spinner", fadeOut, resetTransition],
                ["#funnels-check-svg", fadeOut, resetTransition],
                ["#funnels-fb--color", fadeOut, resetTransition],
                ["#funnels-fb--mono", fadeIn, resetTransition],
                ["#funnels-ga--color", fadeOut, resetTransition],
                ["#funnels-ga--mono", fadeIn, resetTransition],
                ["#funnels-tiktok--color", fadeOut, resetTransition],
                ["#funnels-tiktok--mono", fadeIn, resetTransition],
              ],
        );
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, animate]);

  return scope;
}

export function SyncFunnelsAnimation(props: BoxProps) {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useSyncFunnelsAnimation(animationActive);
  const isInView = useInView(scope, { amount: 0.7 });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Box {...props}>
      <AspectRatio
        display={{ base: "block", md: "none" }}
        ratio={1007 / 444}
        bg="white"
        borderTopRadius="lg"
        filter="drop-shadow(0px 2px 12px rgba(16, 24, 40, 0.08))"
        pos="relative"
        w="100%"
      >
        <Image fill src={funnelsMobile} alt="Graph of customer data audiences." sizes="100%" />
      </AspectRatio>

      <ChakraBox
        initial={{ y: 200 }}
        whileInView={{ y: 0, transition: { ease: "easeInOut", duration: 0.7 } }}
        viewport={{ once: true }}
      >
        <AspectRatio
          display={{ base: "none", md: "block" }}
          ratio={1007 / 442}
          pos="relative"
          bg="white"
          borderTopRadius="lg"
          filter="drop-shadow(0px 2px 12px rgba(16, 24, 40, 0.08))"
          overflow="hidden"
          w="100%"
          maxW="5xl"
          mx="auto"
        >
          <Box ref={scope} boxSize="100%" pos="relative">
            <FunnelsGraph w="100%" h="auto" />

            <Flex align="center" pos="absolute" top={20} right={8} w="260px" h="140px">
              <Tooltip
                id="funnels-tooltip"
                pos="absolute"
                opacity={0}
                top={{ base: -4, lg: 0 }}
                right={{ base: -3, lg: 0 }}
                transform="scale(0.9)"
              />

              <DestinationMenu
                id="funnels-menu"
                pos="absolute"
                opacity={0}
                top={{ base: -6, lg: -2 }}
                right={{ base: -3, lg: 0 }}
                transform="scale(0.9)"
              />
            </Flex>
          </Box>
        </AspectRatio>
      </ChakraBox>
    </Box>
  );
}
